import './toast.scss'
import templateUrl from './toast.html'

/**
 * Show a message to the user using $mdToast with a consistent preset
 *
 * @example
 * TmrToast.success('SOME.TRANSLATION.KEY', { someVariable: 'foo' })
 * TmrToast.exception(err)
 *
 * @type {ngService}
 */
export class TmrToast {
  static ngType = 'service'

  constructor($log, $translate, $mdToast, TmrError) {
    'ngInject'
    Object.assign(this, { $log, $translate, $mdToast, TmrError })
  }

  autoHidetoast() {
    return this.$mdToast.tmrToastAutoHide()
  }

  clickToDismissToast() {
    return this.$mdToast.tmrToast().action('OK')
  }

  /**
   * Hides a clickToDismiss toast the second time it's called
   *
   * (Not the first because most toasts are created immediately before a change
   * of state, so we give them a 1 state window of survival.)
   */
  hide() {
    if (this.toast) {
      if (this.toast.$hideNextTime) this.$mdToast.cancel(this.toast)
      this.toast.$hideNextTime = !this.toast.$hideNextTime
    }
  }

  /**
   * Creates a toast with a message
   *
   * @param {string} message The localized message
   * @param {boolean} clickToDismiss If true, the toast will not autoclose but will have an OK button
   * @param {string=} highlightClass
   * @param {string=} icon
   * @param {string=} iconColor
   *
   * @returns mdToast
   */
  buildToast(
    message,
    clickToDismiss,
    highlightClass,
    icon,
    iconColor = 'primary'
  ) {
    return (clickToDismiss ? this.clickToDismissToast() : this.autoHidetoast())
      .textContent(message)
      .highlightClass(highlightClass)
      .icon(icon)
      .iconColor(iconColor)
  }

  /**
   * Shows a toast
   *
   * @param {object} toast A toast created with `TmrToast.buildToast()`
   *
   * @returns mdToast promise
   */
  show(toast) {
    this.toast = this.$mdToast.show(toast).catch(() => {}) // mdToast rejects for no reason when a previos toast is running.
    return this.toast
  }

  /**
   * Shows a toast with a translated message
   *
   * @param {string} message Translation key
   * @param {object} substitutions Translation variable sostitutions
   * @param {string} translateInterpolation Set to 'messageformat' if using translation pluralization
   * @param {boolean} clickToDismiss If true, the toast will not autoclose but will have an OK button
   * @param {string=} highlightClass
   * @param {string=} icon
   * @param {string=} iconColor
   *
   * @returns mdToast promise
   */
  translate(
    message,
    substitutions,
    translateInterpolation,
    clickToDismiss,
    highlightClass,
    icon,
    iconColor
  ) {
    return this.$translate(message, substitutions, translateInterpolation).then(
      message =>
        this.show(
          this.buildToast(
            message,
            clickToDismiss,
            highlightClass,
            icon,
            iconColor
          )
        )
    )
  }

  /**
   * Shows a success toast
   *
   * @param {string} message Translation key
   * @param {object} substitutions Translation variable sostitutions
   * @param {string} translateInterpolation Set to 'messageformat' if using translation pluralization
   * @param {boolean} clickToDismiss If true, the toast will not autoclose but will have an OK button
   */
  success(message, substitutions, translateInterpolation, clickToDismiss) {
    return this.translate(
      message,
      substitutions,
      translateInterpolation,
      clickToDismiss,
      null,
      this.successIcon
    )
  }

  /**
   * Shows an error toast
   *
   * @param {string} message Translation key
   * @param {object} substitutions Translation variable sostitutions
   * @param {string} translateInterpolation Set to 'messageformat' if using translation pluralization
   * @param {boolean} clickToDismiss If true, the toast will not autoclose but will have an OK button
   */
  error(
    message,
    substitutions,
    translateInterpolation = null,
    clickToDismiss = true
  ) {
    return this.translate(
      message,
      substitutions,
      translateInterpolation,
      clickToDismiss,
      'md-warn',
      this.errorIcon,
      'warn'
    )
  }

  /**
   * Shows a toast of an exception and rethrows it immediately
   *
   * @param {object} exception An error object or an exception
   * @param {boolean} [rethrow=true] If true, rethrows the exception after showing the toast
   */
  exception(err, rethrow = true) {
    let translate = false
    let message = ''
    let substitutions = {}

    // Transitions wrap the original error in "detail".
    let error = err.detail ?? err

    // Mock responses by $httpBackend wrap the original error in "data".
    let errorData = error.data ?? error

    // Backend error (may be mocked). Its error code is mapped to a user friendly translated message.
    if (errorData.errorCode) {
      translate = true
      message = `ERROR.SERVER.${errorData.errorCode}`
      Object.assign(substitutions, errorData)
    }
    // Connection error (i.e.: called an unsupported server, missing device manager, etc.)
    else if (error.status && error.statusText) {
      translate = true
      message = 'ERROR.SERVER.GENERIC'
      Object.assign(substitutions, {
        status: error.status,
        statusText: error.statusText,
      })
    }
    // Frontend handled error.
    else if (error instanceof this.TmrError) {
      translate = true
      message = error.message
      Object.assign(substitutions, error.substitutions)
    }
    // Frontend unhandled error.
    else if (error.message) {
      message = error.message
      this.$log.error(error) // Transitions may eat the error, so log it.
    }

    if (translate) this.error(message, substitutions)
    else
      this.show(
        this.buildToast(message, true, 'md-warn', this.errorIcon, 'warn')
      )

    if (rethrow) throw err
  }

  /**
   * Returns the class name of the error toast icon
   *
   * @returns {string}
   */
  get errorIcon() {
    return 'o-important-warning-1'
  }

  /**
   * Returns the class name of the success toast icon
   *
   * @returns {string}
   */
  get successIcon() {
    return 'o-checked-circle-1'
  }
}

/**
 * Configures a toast preset (preset `tmrToast`)
 *
 * @type {ngConfig}
 */
function tmrToastConfig($mdToastProvider) {
  'ngInject'
  let options = $mdTheming => ({
    templateUrl,
    theme: $mdTheming.defaultTheme(),
    controllerAs: '$ctrl',
    bindToController: true,
    position: 'top right',
    toastClass: 'tmr-toast',
    controller: 'tmrToastController',
  })
  $mdToastProvider.addPreset('tmrToast', {
    argOption: 'textContent',
    methods: [
      'textContent',
      'action',
      'highlightClass',
      'theme',
      'parent',
      'icon',
      'iconColor',
    ],
    options: /*@ngInject*/ $mdTheming =>
      Object.assign({ hideDelay: 0 }, options($mdTheming)),
  })
  $mdToastProvider.addPreset('tmrToastAutoHide', {
    argOption: 'textContent',
    methods: [
      'textContent',
      'action',
      'highlightClass',
      'theme',
      'parent',
      'icon',
      'iconColor',
    ],
    options: /*@ngInject*/ $mdTheming => options($mdTheming),
  })
}

export { tmrToastConfig as config }

/**
 * Hides toast when changing state
 *
 * @type {ngRun}
 */
let tmrToastRun = /*@ngInject*/ ($transitions, TmrToast) =>
  $transitions.onSuccess({ exiting: 'app.**' }, () => TmrToast.hide())

export { tmrToastRun as run }
